// src/redux/userFollow/userFollowReducerSlice.js
import { createSlice } from '@reduxjs/toolkit';
import {
  followUserThunk,
  unfollowUserThunk,
  fetchFollowingThunk,
  fetchFollowersThunk
} from './userFollowActionThunks';

const initialState = {
  // Button state: are we following target userId?
  byTargetId: {},

  // Per-target loading/error for snappy UI feedback
  loadingByTargetId: {},
  errorByTargetId: {},

  // Keep a snapshot to rollback on optimistic failures
  __prevByTargetId: {},

  // Optional global flags (kept for compatibility)
  loading: false,
  error: null,

  // Lists (used by "Connections" tabs)
  followingList: [],
  followersList: [],
};

const setPerTargetLoading = (state, userId, value) => {
  state.loadingByTargetId[userId] = !!value;
  // Keep global loading aligned with "any target loading" for backwards compatibility:
  state.loading = Object.values(state.loadingByTargetId).some(Boolean);
};

const clearPerTargetError = (state, userId) => {
  if (userId in state.errorByTargetId) delete state.errorByTargetId[userId];
  // keep legacy error null when we clear a per-target error
  state.error = null;
};

const setPerTargetError = (state, userId, payload) => {
  state.errorByTargetId[userId] = payload || 'Action failed';
  state.error = payload?.message || payload || 'Action failed';
};

const userFollowSlice = createSlice({
  name: 'userFollow',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    // ---------------- FOLLOW (optimistic) ----------------
    builder
      .addCase(followUserThunk.pending, (state, action) => {
        const { userId } = action.meta?.arg || {};
        if (userId == null) return;

        // snapshot & optimistic on
        state.__prevByTargetId[userId] = !!state.byTargetId[userId];
        state.byTargetId[userId] = true;

        clearPerTargetError(state, userId);
        setPerTargetLoading(state, userId, true);
      })
      .addCase(followUserThunk.fulfilled, (state, action) => {
        const { userId } = action.payload || {};
        if (userId == null) return;

        // ensure final state is following regardless of idempotent
        state.byTargetId[userId] = true;

        // cleanup
        delete state.__prevByTargetId[userId];
        setPerTargetLoading(state, userId, false);
        clearPerTargetError(state, userId);
      })
      .addCase(followUserThunk.rejected, (state, action) => {
        const { userId } = action.payload || action.meta?.arg || {};
        if (userId == null) return;

        // rollback
        state.byTargetId[userId] = !!state.__prevByTargetId[userId];
        delete state.__prevByTargetId[userId];

        setPerTargetLoading(state, userId, false);
        setPerTargetError(state, userId, action.payload);
      });

    // ---------------- UNFOLLOW (optimistic) ----------------
    builder
      .addCase(unfollowUserThunk.pending, (state, action) => {
        const { userId } = action.meta?.arg || {};
        if (userId == null) return;

        // snapshot & optimistic off
        state.__prevByTargetId[userId] = !!state.byTargetId[userId];
        state.byTargetId[userId] = false;

        clearPerTargetError(state, userId);
        setPerTargetLoading(state, userId, true);
      })
      .addCase(unfollowUserThunk.fulfilled, (state, action) => {
        const { userId } = action.payload || {};
        if (userId == null) return;

        // ensure final state is not following regardless of idempotent
        state.byTargetId[userId] = false;

        // cleanup
        delete state.__prevByTargetId[userId];
        setPerTargetLoading(state, userId, false);
        clearPerTargetError(state, userId);
      })
      .addCase(unfollowUserThunk.rejected, (state, action) => {
        const { userId } = action.payload || action.meta?.arg || {};
        if (userId == null) return;

        // rollback
        state.byTargetId[userId] = !!state.__prevByTargetId[userId];
        delete state.__prevByTargetId[userId];

        setPerTargetLoading(state, userId, false);
        setPerTargetError(state, userId, action.payload);
      });

    // ---------------- FOLLOWING LIST ----------------
    builder
      .addCase(fetchFollowingThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchFollowingThunk.fulfilled, (state, action) => {
        state.loading = false;
        state.followingList = Array.isArray(action.payload) ? action.payload : [];

        // hydrate button state from list
        state.followingList.forEach((u) => {
          const id = u?.id ?? u?.user_id;
          if (id != null) state.byTargetId[id] = true;
        });
      })
      .addCase(fetchFollowingThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.message || action.payload || 'Failed to fetch following list';
      });

    // ---------------- FOLLOWERS LIST ----------------
    builder
      .addCase(fetchFollowersThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchFollowersThunk.fulfilled, (state, action) => {
        state.loading = false;
        state.followersList = Array.isArray(action.payload) ? action.payload : [];
      })
      .addCase(fetchFollowersThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.message || action.payload || 'Failed to fetch followers list';
      });
  },
});

export default userFollowSlice.reducer;

// ---------------- Selectors ----------------
export const selectIsFollowing = (state, userId) =>
  !!state.userFollow?.byTargetId?.[userId];

export const selectFollowLoadingFor = (state, userId) =>
  !!state.userFollow?.loadingByTargetId?.[userId];

export const selectFollowErrorFor = (state, userId) =>
  state.userFollow?.errorByTargetId?.[userId] ?? null;

export const selectFollowingIds = (state) =>
  new Set(
    (state.userFollow?.followingList || [])
      .map((u) => u?.id ?? u?.user_id)
      .filter(Boolean)
  );

export const selectFollowingUsernames = (state) =>
  new Set(
    (state.userFollow?.followingList || [])
      .map((u) => (u?.username || '').toLowerCase())
      .filter(Boolean)
  );
